import { FormArray, FormGroup } from '@angular/forms';
import { UnitsGridColumn, UnitsGridRowFactory } from './units-grid-input.models';

export function canDeleteUnitsGridRow(args: {
  readonly allowDeleteRows: boolean;
  readonly rowCount: number;
  readonly minRows: number;
  readonly rowIndex: number;
}): boolean {
  if (!args.allowDeleteRows) {
    return false;
  }

  return args.rowCount > args.minRows && args.rowIndex >= 0;
}

export function insertUnitsGridRowAbove(
  rows: FormArray<FormGroup>,
  rowFactory: UnitsGridRowFactory | null,
  rowIndex: number
): number | null {
  if (!rowFactory || rowIndex < 0) {
    return null;
  }

  const insertIndex = Math.min(rowIndex, rows.length);
  rows.insert(insertIndex, rowFactory());
  return insertIndex;
}

export function insertUnitsGridRowBelow(
  rows: FormArray<FormGroup>,
  rowFactory: UnitsGridRowFactory | null,
  rowIndex: number
): number | null {
  if (!rowFactory || rowIndex < 0) {
    return null;
  }

  const insertIndex = Math.min(rowIndex + 1, rows.length);
  rows.insert(insertIndex, rowFactory());
  return insertIndex;
}

export function resetUnitsGridRows(
  rows: FormArray<FormGroup>,
  rowFactory: UnitsGridRowFactory | null,
  baselineRows: number
): void {
  rows.clear();

  if (!rowFactory || baselineRows <= 0) {
    return;
  }

  for (let i = 0; i < baselineRows; i++) {
    rows.push(rowFactory());
  }
}

export function ensureUnitsGridRow(
  rows: FormArray<FormGroup>,
  rowFactory: UnitsGridRowFactory | null,
  rowIndex: number
): FormGroup | null {
  if (rowIndex < rows.length) {
    return rows.at(rowIndex) as FormGroup;
  }

  if (!rowFactory) {
    return null;
  }

  while (rows.length <= rowIndex) {
    rows.push(rowFactory());
  }

  return rows.at(rowIndex) as FormGroup;
}

export function buildUnitsGridRawRows(rows: readonly FormGroup[]): Record<string, unknown>[] {
  return rows.map((row) => row.getRawValue() as Record<string, unknown>);
}

export function buildUnitsGridCompareData(
  rows: readonly FormGroup[],
  columns: readonly UnitsGridColumn[]
): Record<string, unknown>[] {
  return rows.map((row) => {
    const output: Record<string, unknown> = {};
    columns.forEach((column) => {
      output[column.field] = row.get(column.field)?.value;
    });

    return output;
  });
}
